import { Stack } from '@mui/material';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useCentralAssortmentFilters } from '@retail/my-assortment/context';
import {
  useExportCentralAssortmentToExcel,
  useFetchCentralAssortment,
  useFetchCentralAssortmentFilters,
} from '@retail/my-assortment/data-access';
import { CentralHierarchyFilterContext, UserName } from '@retail/my-assortment/types';
import { useDisclosure } from '@shared/hooks';
import { AssortmentToolbar } from '../AssortmentToolbar';
import { AddItemsToAssortment } from '../../AddItemsToAssortment';
import { CentralAssortmentItemsContainer } from './CentralAssortmentItemsContainer';
import { useCentralAssortmentItems } from './useCentralAssortmentItems';
import {
  ErrorFallback,
  FilterChipContainer,
  SentryErrorBoundary,
  ToggleFiltersButton,
  ToggleFiltersToolbar,
} from '@retail/components';
import { TotalItemsLoader } from '@retail/products/components';
import { AssortmentItemIdentifiersFilterDialog } from '@retail/products/containers';
import { CentralAssortmentFilters } from '@retail/my-assortment/components';
import { useMemo } from 'react';
import { centralAssortmentsFilters } from '@retail/my-assortment/utils';

export function CentralAssortmentContainer() {
  const { assortmentId, appTenant } = useSelectedOrgUnit();

  const { data: filterOptionsDTO } = useFetchCentralAssortmentFilters({
    assortmentId,
    suspense: false,
  });

  const filterOptions = useMemo(
    () => (filterOptionsDTO ? CentralHierarchyFilterContext.fromDto(filterOptionsDTO) : undefined),
    [filterOptionsDTO]
  );

  const {
    suppliers,
    productGroupHierarchy,
    subAssortments,
    selectedSubAssortments,
    setFilters,
    updateFilter,
  } = useCentralAssortmentFilters(filterOptionsDTO);

  const { isOpen: isAddItemsDialogOpen, onToggle: toggleAddItemsDialog } = useDisclosure();
  const { isOpen: showFilters, onToggle: toggleFilters } = useDisclosure(true);
  const itemIdentifiersFilterDialog = useDisclosure(false);

  const {
    items: centralAssortmentItems,
    filteredItems: filteredAssortmentItems,
    totalNumberOfItems,
    foundNumberOfItems,
    isFetching: isFetchingItems,
    isError: isErrorFetchItems,
  } = useCentralAssortmentItems({ suspense: false });

  const { isLoading, exportToExcel } = useExportCentralAssortmentToExcel({
    items: centralAssortmentItems,
  });

  const availableUserNames = useMemo(() => {
    const uniqeUserNames: UserName[] = [];

    filteredAssortmentItems?.forEach((item) => {
      const createdBy = item.createdBy;
      if (
        createdBy &&
        !uniqeUserNames.map((userName) => userName.iamId).includes(createdBy.iamId)
      ) {
        uniqeUserNames.push(createdBy);
      }
    });
    return uniqeUserNames;
  }, [filteredAssortmentItems]);

  return (
    <Stack flexGrow={1} overflow="hidden">
      <AssortmentToolbar
        exportToExcel={exportToExcel}
        isLoadingExport={isLoading}
        totalItems={filteredAssortmentItems.length}
        assortmentTagSelect={{
          assortmentTags: subAssortments,
          selectedTags: selectedSubAssortments,
          selectTagById: (tagId: number) => updateFilter('subAssortmentIds', tagId.toString()),
          resetTags: () => setFilters({ subAssortmentIds: undefined }),
        }}
        onToggleAddItemsDialog={toggleAddItemsDialog}
      />

      <ToggleFiltersToolbar sx={{ justifyContent: 'space-between', mb: 1 }}>
        <FilterChipContainer
          allowedKeys={
            appTenant.hasMainSupplier
              ? centralAssortmentsFilters
              : centralAssortmentsFilters.filter((key) => key !== 'fromMainSupplier')
          }
          filterOptions={{
            ...filterOptions,
            subAssortments: filterOptions?.assortmentTags,
            availableUsers: availableUserNames.map((user) => {
              return { iamId: user.iamId, name: `${user.givenName} ${user.familyName}` };
            }),
          }}
        />
        <Stack flexDirection="row" alignItems="flex-end" gap={2}>
          {!isErrorFetchItems && (
            <TotalItemsLoader
              totalItems={totalNumberOfItems}
              itemsShowing={foundNumberOfItems}
              isFetching={isFetchingItems}
            />
          )}
          <ToggleFiltersButton showFilters={showFilters} toggleFilters={toggleFilters} />
        </Stack>
      </ToggleFiltersToolbar>
      <Stack flexGrow={1} overflow="hidden" flexDirection="row">
        <SentryErrorBoundary
          fallback={({ error, eventId, resetError }) => (
            <Stack flexGrow={1} justifyContent="center">
              <ErrorFallback message={error.message} eventId={eventId} resetError={resetError} />
            </Stack>
          )}
        >
          <CentralAssortmentItemsContainer
            assortmentId={assortmentId}
            externalItemSource={appTenant.externalItemSource}
            toggleAddItemsDialog={toggleAddItemsDialog}
          />
        </SentryErrorBoundary>

        <CentralAssortmentFilters
          availableUserNames={availableUserNames}
          showFilters={showFilters}
          suppliers={suppliers}
          productGroupHierarchy={productGroupHierarchy}
          selectedSubAssortments={selectedSubAssortments}
          hideInAssortment={true}
          showIncludeExpiredTags
          showFromMainSupplier={appTenant.hasMainSupplier}
          onOpenItemIdentifiersFilter={itemIdentifiersFilterDialog.onOpen}
        />
      </Stack>
      {isAddItemsDialogOpen && (
        <AddItemsToAssortment open onClose={toggleAddItemsDialog} assortmentId={assortmentId} />
      )}
      {itemIdentifiersFilterDialog.isOpen && (
        <AssortmentItemIdentifiersFilterDialog
          assortmentId={assortmentId}
          useFetchItems={useFetchCentralAssortment}
          onApply={(itemIdentifiers) => {
            setFilters({ itemIdentifiers });
            itemIdentifiersFilterDialog.onClose();
          }}
          onClose={itemIdentifiersFilterDialog.onClose}
        />
      )}
    </Stack>
  );
}
